<template>
    <div :class="wrapperClasses">
        <div ref="gutter" :class="gutterClasses">
            <div
                v-for="line in lineNumbers"
                :key="'code-preview-line-' + line"
                :class="lineNumberClasses"
            >
                {{ line }}
            </div>
        </div>
        <textarea
            ref="textarea"
            :value="modelValue"
            :class="textareaClasses"
            readonly
            spellcheck="false"
            wrap="off"
            @scroll="syncScroll"
        ></textarea>
    </div>
</template>

<script>
export default {
    name: "CodePreviewEditor",
    props: {
        modelValue: {
            type: String,
            default: '',
        },
        variant: {
            type: String,
            default: 'vault',
        },
        fill: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        isVaultVariant() {
            return this.variant === 'vault';
        },
        wrapperClasses() {
            return [
                this.isVaultVariant ? 'vault-code-preview' : 'code-preview-editor',
                {'vault-code-preview--fill': this.isVaultVariant && this.fill},
            ];
        },
        gutterClasses() {
            return this.isVaultVariant ? 'vault-code-preview__gutter' : 'code-preview-editor__gutter';
        },
        lineNumberClasses() {
            return this.isVaultVariant ? 'vault-code-preview__line-number' : 'code-preview-editor__line-number';
        },
        textareaClasses() {
            return this.isVaultVariant ? 'vault-code-preview__textarea' : 'code-preview-editor__textarea';
        },
        lineNumbers() {
            const lineCount = Math.max(1, String(this.modelValue || '').replace(/\r\n/g, '\n').split('\n').length);
            return Array.from({length: lineCount}, (_, index) => index + 1);
        },
    },
    methods: {
        syncScroll() {
            const textarea = this.$refs.textarea;
            const gutter = this.$refs.gutter;
            if (!textarea || !gutter) {
                return;
            }

            gutter.scrollTop = textarea.scrollTop;
        },
    },
};
</script>

<style scoped>
.code-preview-editor {
    display: flex;
    min-height: 10rem;
    max-height: 22rem;
    overflow: hidden;
    border: 1px solid var(--ui-border-subtle);
    border-radius: 0.375rem;
    background: #ffffff;
}

.code-preview-editor__gutter {
    flex: 0 0 56px;
    width: 56px;
    overflow: hidden;
    background: var(--ui-surface-raised);
    border-right: 1px solid var(--ui-border-subtle);
    color: var(--ui-text-muted);
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 12px;
    line-height: 1.55;
    padding: 12px 8px 12px 0;
    text-align: right;
    user-select: none;
}

.code-preview-editor__line-number {
    height: 1.55em;
}

.code-preview-editor__textarea {
    flex: 1 1 auto;
    width: 100%;
    min-height: 10rem;
    max-height: 22rem;
    border: 0;
    outline: 0;
    resize: none;
    background: #ffffff;
    color: var(--ui-text-heading);
    overflow: auto;
    white-space: pre;
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 13px;
    line-height: 1.55;
    padding: 12px 14px;
}

.code-preview-editor__textarea::selection {
    background: rgba(96, 165, 250, 0.28);
}

.vault-code-preview {
    display: flex;
    width: 100%;
    min-height: 12rem;
    height: 12rem;
    background: #ffffff;
    overflow: hidden;
}

.vault-code-preview--fill {
    min-height: 100%;
    height: 100%;
}

.vault-code-preview__gutter {
    flex: 0 0 56px;
    width: 56px;
    overflow: hidden;
    background: var(--ui-surface-raised);
    border-right: 1px solid var(--ui-border-subtle);
    color: var(--ui-text-muted);
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 12px;
    line-height: 1.55;
    padding: 12px 8px 12px 0;
    text-align: right;
    user-select: none;
}

.vault-code-preview__line-number {
    height: 1.55em;
}

.vault-code-preview__textarea {
    flex: 1 1 auto;
    width: 100%;
    height: 100%;
    border: 0;
    outline: 0;
    resize: none;
    background: #ffffff;
    color: var(--ui-text-heading);
    overflow: auto;
    white-space: pre;
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 13px;
    line-height: 1.55;
    padding: 12px 14px;
}

.vault-code-preview__textarea::selection {
    background: rgba(59, 130, 246, 0.25);
}
</style>
